import React, { FC } from "react";
import { IStationDiagRow, IStationDiagTable } from "../../../models/IUser";
import StationDiagService from "../../../services/StationDiagService";
import { useModuleStationDiag } from "./hooks/useModuleStationDiag";

/**
 * `п. 3` резерва (`ПИВ_07` 0.35, разд. 1; `FW-161`): диагностика станций модуля.
 *
 * Показывает **последний** кадр `station_diag` по каждой станции: причина
 * остановки словами, сбросы с включения, время работы, отказы контактора, потери
 * связи. Кадр приходит по изменению, поэтому живого обновления нет — данные
 * читаются один раз на открытие карточки.
 *
 * Числа переводит сервер (коды причин — его дело), поэтому здесь только показ:
 * незнакомый код приходит как «код N» и показывается как есть, у станции без
 * кадров — «нет данных», а не нули.
 */

/** Чтение таблицы у сервера. Отдельной функцией — так тест подменяет службу. */
async function fetchStationDiag(moduleId: number): Promise<IStationDiagTable> {
  const response = await StationDiagService.fetchForModule(moduleId);
  return response.data;
}

/** Время кадра в читаемом виде; null — кадра нет. */
export function formatFrameTime(at: string | null): string | null {
  if (!at) return null;

  const date = new Date(at);

  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("ru-RU");
}

/**
 * Время работы изделия: секунды → «17 ч 5 мин», «45 с».
 *
 * Дни не выводим: изделие перезагружается чаще, а «1 д 2 ч» читается хуже, чем
 * «26 ч». Секунды нужны только там, где минут ещё нет.
 */
export function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "—";

  const total = Number(seconds);

  if (!Number.isFinite(total)) return "—";

  const whole = Math.max(0, Math.floor(total));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);

  if (hours > 0) return minutes > 0 ? `${hours} ч ${minutes} мин` : `${hours} ч`;
  if (minutes > 0) return `${minutes} мин`;

  return `${whole} с`;
}

/**
 * Сбросы с включения и биты причины последнего сброса (`reset_cause`, регистр
 * 0x24). Имён бит нам ещё не назвали, поэтому показываем числами: `1 · биты 3`.
 */
export function resetCountText(row: IStationDiagRow): string {
  if (row.resetCount === null || row.resetCount === undefined) return "—";

  const bits = row.resetCauseBits?.length
    ? ` · биты ${row.resetCauseBits.join(", ")}`
    : "";

  return `${row.resetCount}${bits}`;
}

/** Станция в строке: «№2 · 12» (номер в модуле и место установки). */
export function stationLabel(row: IStationDiagRow): string {
  const number =
    row.stationNumber === null || row.stationNumber === undefined
      ? "—"
      : `№${row.stationNumber}`;

  return row.place ? `${number} · ${row.place}` : number;
}

function numberCell(value: number | null, hasData: boolean): string {
  if (!hasData) return "—";

  return value === null || value === undefined ? "—" : String(value);
}

export const PopupModuleStationDiag: FC<{ moduleId: number }> = ({
  moduleId,
}) => {
  const { table, isLoading, error } = useModuleStationDiag({
    moduleId,
    fetchTable: fetchStationDiag,
  });

  const frameTime = formatFrameTime(table?.at ?? null);

  return (
    <div className="popup_module_diag">
      <div className="popup_module_diag_head">
        ДИАГНОСТИКА СТАНЦИЙ
        {frameTime ? (
          <span className="popup_module_diag_at"> · кадр {frameTime}</span>
        ) : null}
      </div>

      {isLoading ? <div className="popup_module_diag_hint">читаем…</div> : null}

      {error ? <div className="popup_module_diag_error">{error}</div> : null}

      {!isLoading && !error && table ? (
        table.stations.length === 0 ? (
          <div className="popup_module_diag_hint">
            у модуля нет станций — диагностировать нечего
          </div>
        ) : (
          <table className="popup_module_diag_table">
            <thead>
              <tr>
                <th>станция</th>
                <th>причина остановки</th>
                <th>сбросы с включения</th>
                <th>время работы</th>
                <th>отказы контактора</th>
                <th>потери связи</th>
              </tr>
            </thead>
            <tbody>
              {table.stations.map((row, index) => (
                <tr
                  key={row.stationId ?? `st-${row.stationNumber ?? index}`}
                  title={
                    formatFrameTime(row.at)
                      ? `кадр ${formatFrameTime(row.at)}`
                      : "кадров по станции нет"
                  }
                >
                  <td>{stationLabel(row)}</td>
                  <td>{row.hasData ? row.stopReasonText ?? "—" : "нет данных"}</td>
                  <td>{resetCountText(row)}</td>
                  <td>{row.hasData ? formatUptime(row.uptimeS) : "—"}</td>
                  <td>{numberCell(row.pswFail, row.hasData)}</td>
                  <td>{numberCell(row.linkLoss, row.hasData)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </div>
  );
};
